import to from 'await-to-js'
import { AxiosError } from 'axios'

import { saveBusinessProtectionProducts } from '../../api/businessProtection'
import {
  addCaseProtectionProduct,
  createProtectionProduct,
  deleteCaseProtectionProduct,
  getProtectionProduct,
  updateProtectionProduct,
} from '../../api/protection_product'
import {
  checkAuthProtection,
  checkAuthProtectionV2,
  saveProtectionProductsToCase,
  saveProtectionProductsToCaseV2,
  splitProductEstimate,
  splitProductEstimateV2,
  syncProtectionDocuments,
  syncProtectionDocumentsV2,
} from '../../api_protection/methods'
import {
  ProtectionProduct,
  ProtectionProductUpdateInput,
  Resolvers,
  SplitCoverBenefitsInput,
  SyncProtectionDocumentsResponse,
} from '../../generated/resolvers'
import { CdmGetProtectionProductRequest } from '../../service/luther/model'
import { setProtectionProduct } from './ProtectionProduct.helpers'

const ERROR_COULD_NOT_SAVE = 'Could not save. Please try again or contact support.'
const ERROR_COULD_NOT_REMOVE_RECOMMENDATION = 'Could not remove recommendation. Please try again or contact support.'

const resolvers: Resolvers = {
  Query: {
    protectionProducts: async (_, variables) => {
      const data = await getProtectionProduct(variables as CdmGetProtectionProductRequest)
      return data?.protection_products as ProtectionProduct[]
    },
    protectionProductsForClients: async (_, variables) => {
      const { protectionProductDetails, filterClientIds } = variables

      //we need to use looped fetch,because the API is designed so that
      //if you request products for both clients at once it searches for
      //products with both clients on the product
      const promises = await Promise.allSettled(
        filterClientIds.map(async (id) => {
          const res = await getProtectionProduct({
            protectionProductDetails,
            filter_client_ids: [id],
          } as CdmGetProtectionProductRequest)

          return res
        }),
      )

      //fitler out the rejected responses
      const fullfilled = promises.filter(
        (promise) => promise.status === 'fulfilled' && Object.keys(promise.value).length !== 0,
      )
      // merge the fullfilled ones
      const merged = fullfilled.flatMap((promise) => {
        if (promise.status === 'fulfilled') {
          return promise.value.protection_products
        }
      })
      // remove duplicates
      const myMap = new Map()
      merged.forEach((el) => {
        myMap.set(el?.protection_id, el)
      })
      const unique = Array.from(myMap.values())

      return unique as ProtectionProduct[]
    },
    isProtectionAuthValid: async (_, { input }) => {
      const res = await checkAuthProtection(input)
      return res?.credentials_valid
    },
    isProtectionAuthValidV2: async (_, { input }) => {
      const res = await checkAuthProtectionV2(input)
      return res?.credentials_valid
    },
    quoteSource: async (_, { input }) => {
      const p = await splitProductEstimate(input as SplitCoverBenefitsInput)

      return p.quotes
    },
    quoteSourceV2: async (_, { input }) => {
      const p = await splitProductEstimateV2(input as SplitCoverBenefitsInput)

      return p.quotes
    },
  },
  Mutation: {
    saveBusinessProtectionProducts: async (_, { input }) => {
      return await saveBusinessProtectionProducts(input)
    },
    saveProtectionProductsToCase: async (_, { inputs }) => {
      return await saveProtectionProductsToCase(inputs)
    },
    saveProtectionProductsToCaseV2: async (_, { inputs }) => {
      return await saveProtectionProductsToCaseV2(inputs)
    },
    saveProtectionProducts: async (_, { inputs }) => {
      return await Promise.all(
        inputs.map(async (input) => {
          const { case_ids, ...rest } = input
          const hasCaseId = case_ids && case_ids.length > 0
          if (!hasCaseId) throw new Error(ERROR_COULD_NOT_SAVE)

          // Create the protection product
          const createBody = { protection_product: { details: rest } }
          // @ts-ignore
          // The tool that generates types from Luther's swagger is making a mistake
          // despite the fact that it's pointed to integration, so we'll ignore it
          // until they've pushed to staging
          const createData = await createProtectionProduct(createBody)
          const { protection_product, exception: createException } = createData
          if (createException) throw new Error(ERROR_COULD_NOT_SAVE)

          // Associate it with the case
          const { protection_id: newId } = protection_product || {}
          const caseId = case_ids![0] // Can assert with '!' because of the return case above
          const linkBody = { case_id: caseId }
          const linkData = await addCaseProtectionProduct(newId!, linkBody)
          const { exception: linkException } = linkData
          if (linkException) throw new Error(ERROR_COULD_NOT_SAVE)

          return protection_product as ProtectionProduct
        }),
      )
    },
    removeProtectionProducts: async (_, { protection_ids, case_id }) => {
      await Promise.all(
        protection_ids.map(async (protection_id) => {
          const res = await deleteCaseProtectionProduct(protection_id, case_id)
          // Display an error message within the toast if something goes wrong
          const { exception } = res
          const exceptionKey = ERROR_COULD_NOT_REMOVE_RECOMMENDATION
          if (exception) throw new Error(exceptionKey)

          return protection_id
        }),
      )
      return protection_ids
    },

    updateProtectionProduct: async (_, { input, protection_id }) => {
      const body: ProtectionProductUpdateInput = {
        details: input,
      }
      return updateProtectionProduct(protection_id, body)
    },
    syncProtectionDocuments: async (_, { input }) => {
      const [error, data] = await to<SyncProtectionDocumentsResponse, AxiosError>(syncProtectionDocuments(input))
      const protectionProduct = setProtectionProduct(data, error)
      return protectionProduct
    },
    syncProtectionDocumentsV2: async (_, { input }) => {
      const [error, data] = await to<SyncProtectionDocumentsResponse, AxiosError>(syncProtectionDocumentsV2(input))
      const protectionProduct = setProtectionProduct(data, error)
      return protectionProduct
    },
  },
}

export default resolvers
